import { impersonationApi, type ImpersonationStartResponse } from '@/api/endpoints/auth';
import { getApiErrorMessage } from '@/api/client';
import {
  clearImpersonationTarget,
  readImpersonationTarget,
} from '@/features/auth/lib/impersonation-target';
import { getSupabaseClient, isSupabaseLoginEnabled } from '@/lib/supabase';
import { setMonitoringUser } from '@/lib/monitoring/identity';
import { useAuthStore } from '@/stores/auth.store';
import type { LoginResponse, UserSession } from '@/types/auth';

/**
 * In-flight guard so React StrictMode double-mount (or getSession +
 * onAuthStateChange) cannot fire two /impersonation/start calls. Concurrent
 * mintSessionForUser magic-links invalidate each other → otp_expired →
 * "Unable to establish session" even when one call would have succeeded.
 */
let inFlight: Promise<boolean> | null = null;

/** Thrown when a parked impersonation target cannot be started (AC-IMP-006/007). */
export class ImpersonationHandoffError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImpersonationHandoffError';
  }
}

/**
 * Map an impersonation start response into the FE UserSession shape.
 */
export function impersonationResponseToSession(res: LoginResponse): UserSession {
  const u = res.user;
  if (!u || !res.accessToken) {
    throw new Error('Impersonation response missing session tokens');
  }
  return {
    userId: u.userId,
    temporaryUsersId: null,
    username: u.email,
    email: u.email,
    token: res.accessToken,
    role: u.role ?? '',
    roleId: 0,
    companyId: u.companyId ?? 0,
    companyName: u.companyName ?? '',
    firstName: u.firstName ?? '',
    lastName: u.lastName ?? '',
    name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim(),
    isActiveDirectory: u.authProvider === 'entra',
    isSessionActivated: true,
    isApproved: true,
    active: true,
    authProvider: u.authProvider,
  };
}

export type ImpersonationHandoffOptions = {
  /**
   * When true (SSO / MFA callback), a failed `/impersonation/start` throws
   * {@link ImpersonationHandoffError} so the caller can sign out and block
   * instead of silently continuing as the SSI operator (AC-IMP-006/007).
   */
  blockOnFailure?: boolean;
};

/**
 * If LoginPage parked a target email, POST /auth/impersonation/start and swap
 * the local + Supabase session to the target. Preserves the SSI operator as
 * `realUser` for the banner. Dedupes concurrent callers via `inFlight`.
 *
 * @returns true when the session was swapped to the target.
 * @throws {ImpersonationHandoffError} when `blockOnFailure` is set and start fails.
 */
export async function runImpersonationHandoff(
  ssiUser: UserSession,
  options: ImpersonationHandoffOptions = {},
): Promise<boolean> {
  if (inFlight) return inFlight;

  const targetEmail = readImpersonationTarget();
  if (!targetEmail) return false;

  // SSI Login with the operator's own email in the field (common "sign in as me"
  // habit) parks a self-target. Impersonating yourself is never valid — clear
  // and continue as the SSI session.
  const selfEmail = ssiUser.email?.trim().toLowerCase() ?? '';
  if (selfEmail && targetEmail === selfEmail) {
    clearImpersonationTarget();
    return false;
  }

  // Claim immediately so a second StrictMode / parallel caller cannot start.
  clearImpersonationTarget();

  inFlight = (async () => {
    const { setTokens, setUser, setRealUser } = useAuthStore.getState();
    try {
      const res: ImpersonationStartResponse = await impersonationApi.start({
        targetEmail,
      });
      if (!res.accessToken || !res.refreshToken) {
        throw new Error('Impersonation response missing session tokens');
      }
      setRealUser(ssiUser);
      setTokens({ accessToken: res.accessToken, refreshToken: res.refreshToken });
      if (isSupabaseLoginEnabled()) {
        const { error } = await getSupabaseClient().auth.setSession({
          access_token: res.accessToken,
          refresh_token: res.refreshToken,
        });
        if (error) throw error;
      }
      setUser(impersonationResponseToSession(res));
      setMonitoringUser(useAuthStore.getState().user!);
      return true;
    } catch (err) {
      const message = getApiErrorMessage(
        err,
        'That account cannot be impersonated. Confirm the email and try again.',
      );
      if (options.blockOnFailure) {
        throw new ImpersonationHandoffError(message);
      }
      // eslint-disable-next-line no-console
      console.error('[impersonation] start failed:', err);
      return false;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}
